import { Fragment } from 'react';
import { cn } from '../../../../lib/utils';
import { primaryText, secondaryText, semiBoldText, smallTextSize } from '../../../../styling/stylingHelper';
import type { ClientTrackerResponse } from '../../../../services/admin/clientAnalyticsService';
import { sumMonths, sumRestaurants, costPctRatio, formatPct, formatPp, formatEuro, trendColor, type Lens } from './trackerUtils';

interface OverviewViewProps {
  data: ClientTrackerResponse;
  lens: Lens;
}

export function OverviewView({ data, lens }: OverviewViewProps) {
  const allRestaurants = data.clients.flatMap((c) => c.restaurants);
  const allMonths = data.months;

  return (
    <table className="w-full text-sm border-collapse">
      <thead>
        <tr className="border-b border-gray-200 dark:border-gray-700">
          <th className={cn('text-left font-medium px-3 py-2', secondaryText)}>Client / Restaurant</th>
          <th className={cn('text-right font-medium px-3 py-2', secondaryText)}>Omzet</th>
          <th className={cn('text-right font-medium px-3 py-2', secondaryText)}>Inslag</th>
          <th className={cn('text-right font-medium px-3 py-2', secondaryText)}>Cost %</th>
          <th className={cn('text-right font-medium px-3 py-2', secondaryText)}>Months</th>
          <th className={cn('text-left font-medium px-3 py-2', secondaryText)}>CC start</th>
          <th className={cn('text-right font-medium px-3 py-2', secondaryText)}>Before</th>
          <th className={cn('text-right font-medium px-3 py-2', secondaryText)}>After</th>
          <th className={cn('text-right font-medium px-3 py-2', secondaryText)}>Δ</th>
          <th className={cn('text-left font-medium px-3 py-2', secondaryText)}>Trend</th>
        </tr>
      </thead>
      <tbody>
        {data.clients.map((client) => {
          const clientPair = sumRestaurants(client.restaurants, allMonths, lens);
          const clientMonthsFilled = client.restaurants.reduce((sum, r) => sum + r.monthsFilled, 0);
          return (
            <Fragment key={client.clientId}>
              <tr className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                <td className={cn('px-3 py-2', primaryText, semiBoldText)}>{client.clientName}</td>
                <td className={cn('px-3 py-2 text-right', semiBoldText, primaryText)}>{formatEuro(clientPair.omzet)}</td>
                <td className={cn('px-3 py-2 text-right', semiBoldText, primaryText)}>{formatEuro(clientPair.inslag)}</td>
                <td className={cn('px-3 py-2 text-right', semiBoldText, primaryText)}>{formatPct(costPctRatio(clientPair))}</td>
                <td className={cn('px-3 py-2 text-right', smallTextSize, secondaryText)}>{clientMonthsFilled}</td>
                <td className={cn('px-3 py-2', smallTextSize, secondaryText)}>{client.ccStartDate ?? '—'}</td>
                <td className="px-3 py-2" colSpan={4} />
              </tr>

              {client.restaurants.map((r) => {
                const pair = sumMonths(r.months, allMonths, lens);
                const ba = r.beforeAfter;
                return (
                  <tr key={r.restaurantId} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/30">
                    <td className={cn('px-3 py-2', smallTextSize, primaryText)}>
                      <span className="pl-5 block">{r.restaurantName}</span>
                    </td>
                    <td className={cn('px-3 py-2 text-right', smallTextSize, primaryText)}>{formatEuro(pair.omzet)}</td>
                    <td className={cn('px-3 py-2 text-right', smallTextSize, primaryText)}>{formatEuro(pair.inslag)}</td>
                    <td className={cn('px-3 py-2 text-right', smallTextSize, primaryText)}>{formatPct(costPctRatio(pair))}</td>
                    <td className={cn('px-3 py-2 text-right', smallTextSize, secondaryText)}>{r.monthsFilled}</td>
                    <td className={cn('px-3 py-2', smallTextSize, secondaryText)}>{ba?.ccStartDate ?? '—'}</td>
                    <td className={cn('px-3 py-2 text-right', smallTextSize, secondaryText)}>{formatPct(ba?.costPctBefore ?? null)}</td>
                    <td className={cn('px-3 py-2 text-right', smallTextSize, secondaryText)}>{formatPct(ba?.costPctAfter ?? null)}</td>
                    <td
                      className={cn(
                        'px-3 py-2 text-right',
                        smallTextSize,
                        semiBoldText,
                        ba?.deltaPp != null && ba.deltaPp > 0
                          ? 'text-green-600 dark:text-green-400'
                          : ba?.deltaPp != null && ba.deltaPp < 0
                            ? 'text-red-600 dark:text-red-400'
                            : secondaryText
                      )}
                    >
                      {formatPp(ba?.deltaPp ?? null)}
                    </td>
                    <td className={cn('px-3 py-2', smallTextSize, trendColor(ba?.trend ?? null))}>{ba?.trend ?? '—'}</td>
                  </tr>
                );
              })}
            </Fragment>
          );
        })}

        <tr className="border-t-2 border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-800">
          <td className={cn('px-3 py-2', primaryText, semiBoldText)}>TOTAL — all clients</td>
          {(() => {
            const pair = sumRestaurants(allRestaurants, allMonths, lens);
            return (
              <>
                <td className={cn('px-3 py-2 text-right', semiBoldText, primaryText)}>{formatEuro(pair.omzet)}</td>
                <td className={cn('px-3 py-2 text-right', semiBoldText, primaryText)}>{formatEuro(pair.inslag)}</td>
                <td className={cn('px-3 py-2 text-right', semiBoldText, primaryText)}>{formatPct(costPctRatio(pair))}</td>
              </>
            );
          })()}
          <td className="px-3 py-2" colSpan={6} />
        </tr>
      </tbody>
    </table>
  );
}
